import { OnboardingItemLayout } from "@/components/Onboarding/components/OnboardingItemLayout";
import { Button } from "@/components/ui/button";
import {
  Action,
  ControlType,
  useOnboardingAnalytics,
} from "@/context/AnalyticsProvider";
import { useOnboardingStep } from "@/context/OnboardingStepProvider/OnboardingStepProvider";
import { cn } from "@/lib/utils";
import { ArrowRight } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { OnboardingStep, type OnboardingStepProps } from "../constants";

const GENRE_TYPES = [
  { label: "Action", value: "Action" },
  { label: "Adventure", value: "Adventure" },
  { label: "Fighting", value: "Fighting" },
  { label: "Platformer", value: "Platformer" },
  { label: "Puzzle", value: "Puzzle" },
  { label: "RPG", value: "Role Playing Game" },
  { label: "Simulation", value: "Simulation" },
  { label: "Sports", value: "Sports" },
  { label: "Strategy", value: "Strategy" },
];

export const GenreSelector = ({ visible }: OnboardingStepProps) => {
  const { goToNextStep } = useOnboardingStep();
  const { track } = useOnboardingAnalytics();
  const { currentStep } = useOnboardingStep();
  const hasViewedRef = useRef(false);

  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);

  useEffect(() => {
    if (hasViewedRef.current || !visible) return;
    hasViewedRef.current = true;

    track({
      action: Action.VIEWED,
      extras: {
        id: OnboardingStep.GENRE_SELECTOR,
        step: currentStep,
      },
    });
  }, [track, visible, currentStep]);

  const handleSelection = (genre: string) => {
    let newSelectedGenres: string[];

    if (selectedGenres.includes(genre)) {
      // Remove genre if already selected
      newSelectedGenres = selectedGenres.filter((g) => g !== genre);
    } else if (selectedGenres.length < 3) {
      // Add genre if under limit
      newSelectedGenres = [...selectedGenres, genre];
    } else {
      // Already at limit, don't add
      return;
    }

    setSelectedGenres(newSelectedGenres);

    // Track with final state after selection/deselection
    track({
      action: Action.PRESSED,
      extras: {
        id: OnboardingStep.GENRE_SELECTOR,
        step: currentStep,
        control: JSON.stringify(newSelectedGenres),
        controlType: ControlType.BUTTON,
      },
    });
  };

  const onClick = (genre: string) => {
    // Prevent action if button should be disabled
    const isSelected = selectedGenres.includes(genre);
    const isDisabled = !isSelected && selectedGenres.length >= 3;

    if (isDisabled) {
      return;
    }

    handleSelection(genre);
  };

  const handleNext = () => {
    if (selectedGenres.length > 0) goToNextStep();
  };

  return (
    <OnboardingItemLayout className="justify-center text-center gap-[clamp(0.75rem,3vh,2rem)]">
      {/* Header */}
      <div className="flex flex-col gap-[clamp(0.25rem,1vh,0.5rem)] items-center text-center">
        <h1 className="font-bold text-[clamp(1.25rem,3.5vh,1.75rem)] leading-[1.1]">
          What are your favourite game genres?
        </h1>
        <p className="text-white/70 text-[clamp(0.75rem,2vh,0.875rem)]">
          Choose up to 3 genres ({selectedGenres.length}/3 selected)
        </p>
      </div>
      {/* Genre Selection Grid */}
      <div className="flex flex-wrap justify-center gap-[clamp(0.5rem,2vh,0.75rem)] w-full max-w-md">
        {GENRE_TYPES.map((genre) => {
          const isSelected = selectedGenres.includes(genre.value);
          const isDisabled = !isSelected && selectedGenres.length >= 3;

          return (
            <Button
              key={genre.value}
              variant="ghost"
              size="lg"
              onClick={() => onClick(genre.value)}
              disabled={isDisabled}
              className={cn(
                "w-[48%] py-[clamp(0.5rem,2vh,1.25rem)] px-[clamp(0.75rem,3vw,1rem)] text-[clamp(0.875rem,2.5vh,1rem)] font-medium transition-all duration-200 border-0 rounded-[32px]",
                isSelected
                  ? "bg-[#ABF790] text-gray-900 shadow-md hover:bg-[#ABF790] hover:text-gray-900"
                  : isDisabled
                    ? "bg-white/5 text-white/50 cursor-not-allowed opacity-50"
                    : "bg-white/10 text-white hover:bg-white/20",
              )}
            >
              <span
                className={cn(
                  "text-[clamp(0.875rem,2.5vh,1rem)]",
                  isSelected
                    ? "text-gray-900"
                    : isDisabled
                      ? "text-white/50"
                      : "text-white",
                )}
              >
                {genre.label}
              </span>
            </Button>
          );
        })}
      </div>
      <Button
        size="lg"
        className={`w-full text-[clamp(0.875rem,2.5vh,1rem)] font-semibold py-[clamp(0.75rem,3vh,1rem)] ${
          selectedGenres.length === 0 ? "opacity-50 cursor-not-allowed" : ""
        }`}
        onClick={handleNext}
        disabled={selectedGenres.length === 0}
      >
        Next
        <ArrowRight className="absolute flex-shrink-0 right-8 text-xl" />
      </Button>
    </OnboardingItemLayout>
  );
};
